import { api } from './api'

interface Part { n: number; etag: string }
interface Init { partSize: number; partCount: number; done: Part[]; uploaded?: boolean }
export interface UploadState { sent: number; total: number; error?: string; done: boolean }
const jobs = new Map<string, { promise: Promise<void>; state: UploadState }>()
const listeners = new Set<() => void>()
export const subscribeUploads = (fn: () => void) => { listeners.add(fn); return () => { listeners.delete(fn) } }
export const importUploadState = (id: string) => jobs.get(id)?.state
const emit = () => { for (const fn of listeners) fn() }
window.addEventListener('beforeunload', (event) => { if ([...jobs.values()].some((j) => !j.state.done && !j.state.error)) { event.preventDefault(); event.returnValue = '' } })

let activeParts = 0
const waitingParts: (() => void)[] = []
async function uploadSlot<T>(fn: () => Promise<T>): Promise<T> {
  await new Promise<void>((resolve) => { if (activeParts < 4) { activeParts++; resolve() } else waitingParts.push(resolve) })
  try { return await fn() } finally { const next = waitingParts.shift(); if (next) next(); else activeParts-- }
}

function delay(ms: number, signal?: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) return reject(signal.reason)
    const abort = () => { clearTimeout(timer); reject(signal?.reason) }
    const timer = setTimeout(() => { signal?.removeEventListener('abort', abort); resolve() }, ms)
    signal?.addEventListener('abort', abort, { once: true })
  })
}
/** The proxy fallback retains multipart resume and four concurrent parts without an SDK dependency. */
export function uploadImportFile(file: File, fileId: string, onProgress?: (sent: number) => void, signal?: AbortSignal): Promise<void> {
  const existing = jobs.get(fileId)
  if (existing && !existing.state.error) return existing.promise
  const state: UploadState = { sent: 0, total: file.size, done: false }
  const promise = (async () => {
    const init = await api<Init>(`/api/import-files/${fileId}/upload`, { method: 'POST', signal })
    if (init.uploaded) { state.sent = file.size; state.done = true; onProgress?.(state.sent); emit(); return }
    const done = new Map(init.done.map((p) => [p.n, p]))
    const sizeOf = (n: number) => Math.min(init.partSize, file.size - (n - 1) * init.partSize)
    state.sent = init.done.reduce((s, p) => s + sizeOf(p.n), 0)
    emit(); onProgress?.(state.sent)
    let next = 1
    await Promise.all(Array.from({ length: 4 }, async () => {
      while (next <= init.partCount) {
        const n = next++
        if (done.has(n)) continue
        for (let attempt = 0; ; attempt++) {
          try {
            const part = await uploadSlot(() => api<Part>(`/api/import-files/${fileId}/parts/${n}`, { method: 'PUT', body: file.slice((n - 1) * init.partSize, n * init.partSize), signal, headers: { 'content-type': 'application/octet-stream' } }))
            done.set(n, part); state.sent += sizeOf(n); onProgress?.(state.sent); emit(); break
          } catch (err) {
            if (signal?.aborted || attempt >= 5) throw err
            await delay(Math.min(16000, 1000 * 2 ** attempt), signal)
          }
        }
      }
    }))
    await api(`/api/import-files/${fileId}/complete`, { method: 'POST', body: { parts: [...done.values()].sort((a, b) => a.n - b.n) }, signal })
    state.done = true; emit()
  })().catch((err: Error) => { state.error = err.message; emit(); throw err })
  jobs.set(fileId, { promise, state }); emit()
  return promise
}
